import { absoluteUrl } from "@/lib/seo";

const BUSINESS_ID = absoluteUrl("/#business");
const WEBSITE_ID = absoluteUrl("/#website");
const PERSON_ID = absoluteUrl("/#therapeutin");

/** LocalBusiness / MedicalBusiness – Kerndaten der Praxis */
export const localBusinessJsonLd = {
  "@context": "https://schema.org",
  "@type": ["MedicalBusiness", "LocalBusiness", "HealthAndBeautyBusiness"],
  "@id": BUSINESS_ID,
  name: "PRAXIS IB",
  alternateName: "Praxis IB – Komplementärtherapie Wien",
  description:
    "Komplementärtherapie in Wien: Mikrobiom, Darmgesundheit, Mikronährstofftherapie und Phytotherapie. Ganzheitliche Begleitung – Termine ausschließlich nach Vereinbarung.",
  url: absoluteUrl("/"),
  telephone: "+436604041993",
  email: "office@ibpraxis.net",
  image: absoluteUrl("/og.png"),
  logo: absoluteUrl("/og.png"),
  priceRange: "€€",
  currenciesAccepted: "EUR",
  paymentAccepted: "Cash, Bank Transfer",
  isAcceptingNewPatients: true,
  availableLanguage: ["de", "en"],
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5",
    bestRating: "5",
    worstRating: "1",
    reviewCount: 22,
    itemReviewed: { "@id": BUSINESS_ID },
  },
  medicalSpecialty: ["Nutrition", "PreventiveMedicine", "Gastroenterologic"],
  founder: { "@id": PERSON_ID },
  employee: { "@id": PERSON_ID },
  address: {
    "@type": "PostalAddress",
    streetAddress: "Linzer Straße 383/Top 7",
    addressLocality: "Wien",
    addressRegion: "Wien",
    postalCode: "1140",
    addressCountry: "AT",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 48.201675,
    longitude: 16.261723,
  },
  hasMap: "https://www.google.com/maps?q=Linzer+Stra%C3%9Fe+383,+1140+Wien",
  areaServed: [
    { "@type": "City", name: "Wien" },
    { "@type": "Country", name: "Österreich" },
    { "@type": "Place", name: "Online (nach Vereinbarung)" },
  ],
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Friday"],
      description: "Termine ausschließlich nach Vereinbarung",
    },
  ],
  availableService: [
    {
      "@type": "MedicalTherapy",
      name: "Mikrobiom- & Darmgesundheit",
      description:
        "Mikrobiologische Diagnostik und Therapie der intestinalen Mikrobiota als Basis ganzheitlicher Gesundheit.",
    },
    {
      "@type": "MedicalTherapy",
      name: "Mikronährstofftherapie",
      description:
        "Befundorientierte Substitution zur Unterstützung von Stoffwechsel, Immunsystem und Energiehaushalt.",
    },
    {
      "@type": "MedicalTherapy",
      name: "Phytotherapie (TEM, TCM, Ayurveda)",
      description:
        "Pflanzenheilkundliche Begleitung bei chronischen Beschwerden und zur Stärkung der Selbstregulation.",
    },
    {
      "@type": "MedicalTherapy",
      name: "Psychoneuroimmunologie",
      description:
        "Berücksichtigung der Wechselwirkung von Stressachse, Immunsystem und Mikrobiom.",
    },
    {
      "@type": "MedicalTherapy",
      name: "Komplementäre Diagnostik",
      description:
        "Symptomorientierte Auswahl sinnvoller Laborparameter statt unspezifischer Markerpakete.",
    },
    {
      "@type": "MedicalTherapy",
      name: "Holistische Kinesiologie & 5-Elemente-Lehre",
      description: "Ergänzendes Verfahren zur Wahrnehmung individueller Belastungsmuster.",
    },
  ],
  makesOffer: {
    "@type": "Offer",
    price: "139",
    priceCurrency: "EUR",
    description: "Sitzung (ca. 60 Minuten) – persönlich in Wien (Zoom nach Vereinbarung)",
    availability: "https://schema.org/InStock",
  },
  potentialAction: {
    "@type": "ReserveAction",
    name: "Termin anfragen",
    target: {
      "@type": "EntryPoint",
      urlTemplate: absoluteUrl("/#kontakt"),
      inLanguage: "de-AT",
    },
  },
  mainEntityOfPage: { "@id": WEBSITE_ID },
};

/** Beschwerdebilder aus „Wobei kann ich Ihnen helfen?" (echte Krankheitsbilder) */
export const BESCHWERDEN = [
  // Darm
  "Chronische Darmbeschwerden",
  "Reizdarmsyndrom",
  "Entzündliche Darmerkrankungen",
  "Morbus Crohn",
  "Colitis ulcerosa",
  "Nahrungsmittelunverträglichkeiten",
  "Histaminintoleranz",
  "Laktoseintoleranz",
  "Fruktoseintoleranz",
  "SIBO (Dünndarmfehlbesiedelung)",
  "Divertikulitis",
  "Leaky-Gut-Syndrom",
  "Darmdysbiose",
  // Urogenital
  "Endometriose",
  "Interstitielle Zystitis",
  "Chronische Blasenentzündung",
  "Wiederkehrende Harnwegsinfekte",
  "Reizblase",
  "Painful Bladder Syndrome",
  "Chronisches Beckenschmerzsyndrom (CPPS)",
  "Chronische Prostatitis",
  "Prostatabeschwerden",
  // Haut
  "Urtikaria (Nesselsucht)",
  "Neurodermitis",
  "Ekzeme",
  "Schuppenflechte (Psoriasis)",
  "Rosacea",
  "Akne",
  // Bewegungsapparat & Schmerz
  "Rheumatologische Erkrankungen",
  "Reaktive Arthritis",
  "Arthrose",
  "Psoriasis-Arthritis",
  "Fibromyalgie",
  "Weichteilrheuma",
  "Gelenkschmerzen",
  "Chronische Schmerzzustände",
  // Hormone & Stoffwechsel
  "Schilddrüsenunterfunktion",
  "Hashimoto-Thyreoiditis",
  "Hormonelle Dysbalancen",
  "PMS und Zyklusbeschwerden",
  "Wechselbeschwerden",
  "Eisenmangel",
  // Immunsystem & Infekte
  "Autoimmunerkrankungen",
  "Allergien",
  "Immunschwäche",
  "Infektanfälligkeit",
  "Wiederkehrende Infektionen",
  "Persistierende Infektionen",
  "Herpesinfektionen",
  "Candida- und Pilzinfektionen",
  "Stille Entzündung (Silent Inflammation)",
  // Mikrobiologie
  "Biofilm-Infektionen",
  "Antibiotikaresistenz",
];

/**
 * Reine Suchbegriffe/Umgangssprache – fließen in Meta-Keywords und knowsAbout,
 * werden aber nicht als MedicalCondition ausgezeichnet.
 */
export const BESCHWERDEN_SYNONYME = [
  "Blähbauch",
  "Verdauungsbeschwerden",
  "Bauchschmerzen ohne Befund",
  "Durchfall und Verstopfung im Wechsel",
  "Dünndarmfehlbesiedlung",
  "Darmsanierung",
  "Mikrobiom Analyse Wien",
  "Stuhlanalyse Wien",
  "ständiger Harndrang",
  "Blasenentzündung kommt immer wieder",
  "Harnkultur negativ",
  "unreine Haut",
  "Juckreiz und Quaddeln",
  "Darm-Haut-Achse",
  "Schmerzen ohne Befund",
  "ständig müde trotz Schlaf",
  "Brain Fog",
  "ständig krank",
  "Schlafprobleme und innere Unruhe",
  "Dauerstress und Cortisol",
  "qPCR Diagnostik",
  "NGS Diagnostik",
  "Lymphozytensubtypisierung",
  "Mikronährstofftherapie Wien",
  "Phytotherapie Wien",
  "Komplementärtherapie Wien",
  "ganzheitliche Therapie Wien",
];

/** Alle Beschwerde- und Suchbegriffe als Keyword-String */
export const BESCHWERDEN_KEYWORDS = [...BESCHWERDEN, ...BESCHWERDEN_SYNONYME].join(", ");


/** Verantwortliche Therapeutin */
export const personJsonLd = {
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": PERSON_ID,
  name: "Isabella Bartak",
  jobTitle: "akad. dipl. zert. komplementärmedizinische Therapeutin",
  worksFor: { "@id": BUSINESS_ID },
  url: absoluteUrl("/"),
  image: absoluteUrl("/og.png"),
  knowsAbout: [
    "Mikrobiom",
    "Darmgesundheit",
    "Mikronährstofftherapie",
    "Phytotherapie",
    "Psychoneuroimmunologie",
    "Komplementärmedizin",
    ...BESCHWERDEN,
    ...BESCHWERDEN_SYNONYME,
  ],
};

/** Website inkl. Suchfunktion des Magazins */
export const webSiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  name: "PRAXIS IB",
  url: absoluteUrl("/"),
  inLanguage: "de-AT",
  publisher: { "@id": BUSINESS_ID },
};

/** Landingpage als medizinische Webseite */
export const homeWebPageJsonLd = {
  "@context": "https://schema.org",
  "@type": "MedicalWebPage",
  "@id": absoluteUrl("/#webpage"),
  url: absoluteUrl("/"),
  name: "Komplementärtherapie Wien | Mikrobiom & Darm – PRAXIS IB",
  isPartOf: { "@id": WEBSITE_ID },
  about: { "@id": BUSINESS_ID },
  inLanguage: "de-AT",
  primaryImageOfPage: absoluteUrl("/og.png"),
  keywords: BESCHWERDEN_KEYWORDS,
  mentions: BESCHWERDEN.map((name) => ({
    "@type": "MedicalCondition",
    name,
  })),
};

